import {
  DashboardState,
  EProjectType,
  EPropertyType,
  IComponent,
  IProperty,
  isAppSupportTypeOfferedOnScreen
} from "@kuika/kuika-cl-model";
import { isReactNativeGenerationOffered } from "../../components/design-tools/app-settings/react-native-feature-gate";
import { filterPlaceableComponents, placementExclusionReason } from "./component-palette";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { CHILD_ROLE_ANNOTATIONS } from "./mcp-tools/child-role-annotations";
import { COMPONENT_PURPOSES } from "./mcp-tools/component-purposes";

interface IChildInfo {
  name: string;
  role: string;
  count: number;
  fixed: boolean;
  isContainer: boolean;
}

interface IComponentCatalogEntry {
  name: string;
  category: string;
  purpose: string | null;
  isReportDesignComponent: boolean;
  isContainer: boolean;
  isFormField: boolean;
  hasValidation: boolean;
  appSupportType: number;
  childrenNames: string[];
  childrenInfo: IChildInfo[];
  inVisibleStyles: string[];
  availableEvents: string[];
  keyProperties: Array<{
    name: string;
    type: string;
    options?: string[];
    defaultValue?: string;
    isValueProp?: boolean;
  }>;
}

function buildCatalogEntry(
  component: IComponent,
  isReportMode: boolean,
  projectType?: number,
  isWebScreen?: boolean
): IComponentCatalogEntry {
  const purposeKey = isReportMode ? `${component.name}__report` : component.name;
  const purpose = COMPONENT_PURPOSES[purposeKey] || COMPONENT_PURPOSES[component.name] || null;

  const properties = component.properties || [];

  const keyProperties = properties
    .filter((p: IProperty) => {
      if (p.isTechnical || p.isDeprecated || p.selectedType === 3) return false;
      return isAppSupportTypeOfferedOnScreen({
        appSupportType: p.appSupportType,
        projectType: projectType as EProjectType | undefined,
        isWebScreen: isWebScreen === true,
        reactNativeOffered: isReactNativeGenerationOffered()
      });
    })
    .slice(0, 5)
    .map((p: IProperty) => {
      const entry: { name: string; type: string; options?: string[]; defaultValue?: string; isValueProp?: boolean } = {
        name: p.name,
        type: EPropertyType[p.selectedType] || String(p.selectedType)
      };
      if (p.options && p.options.length > 0) {
        entry.options = p.options;
      }
      if (p.value != null) {
        entry.defaultValue = String(p.value);
      }
      if (p.name === "value") {
        entry.isValueProp = true;
      }
      return entry;
    });

  const availableEvents = properties.filter((p: IProperty) => p.selectedType === 3).map((p: IProperty) => p.name);

  const inVisibleStyles = component.inVisibleStyles || [];

  const childrenNames = (component.children || []).map((c: IComponent) => c.name || "");

  const childrenInfo: IChildInfo[] = (component.childrenInfo || []).map((child) => {
    const annotation = CHILD_ROLE_ANNOTATIONS[child.name];
    return {
      name: child.name,
      role: annotation?.role || "Auto-created child component",
      count: child.count,
      fixed: child.fixed,
      isContainer: annotation?.isContainer ?? false
    };
  });

  return {
    name: component.name,
    category: component.componentCategory || "",
    purpose,
    isReportDesignComponent: component.isReportDesignComponent || false,
    isContainer: component.blockStyle === 2,
    isFormField: component.wrapWithFormItem || false,
    hasValidation: component.hasValidate || false,
    appSupportType: component.appSupportType || 0,
    childrenNames,
    childrenInfo,
    inVisibleStyles,
    availableEvents,
    keyProperties
  };
}

/**
 * Which catalog components may be placed. The rule itself lives in `component-palette.ts` — the
 * human component panel and the workflow screen builder's palette ask the same question, and three
 * copies of it would drift (KOA-18431).
 */
function getFilteredComponents(
  components: IComponent[],
  isReportMode: boolean,
  projectType?: number,
  isWebScreen?: boolean
): IComponent[] {
  return filterPlaceableComponents(components, {
    projectType,
    isWebScreen: isWebScreen ?? true,
    isReportMode,
    reactNativeOffered: isReactNativeGenerationOffered()
  });
}

function getCategories(
  components: IComponent[],
  isReportMode: boolean,
  projectType?: number,
  isWebScreen?: boolean
): string[] {
  const cats = new Set<string>();
  const filtered = getFilteredComponents(components, isReportMode, projectType, isWebScreen);
  for (const component of filtered) {
    if (component.componentCategory) {
      cats.add(component.componentCategory);
    }
  }
  return Array.from(cats).sort();
}

export function getDesignerComponentCatalog(
  sdInstance: ScreenDesignInstance,
  input: { componentName?: string; category?: string; designMode?: string }
): Record<string, unknown> {
  const sd = getValues(sdInstance);
  const components: IComponent[] | undefined = sd.project?.componentSetObj?.components;
  if (!components) return { error: "Components not loaded" };

  const { componentName, category } = input;
  const designMode = input.designMode || "designer";
  const dashboardState = sd.dashboardState ?? null;
  const isReportMode = designMode === "report" || dashboardState === DashboardState.reportDesigner;

  const projectType = sd.project?.projectType;
  const isWebScreen = sd.getIsWebScreen?.() ?? true;

  const filtered = getFilteredComponents(components, isReportMode, projectType, isWebScreen);

  // Deduplicate by name
  const deduped = new Map<string, IComponent>();
  for (const component of filtered) {
    if (!deduped.has(component.name)) {
      deduped.set(component.name, component);
    }
  }

  // Single component lookup
  if (componentName) {
    // Try exact match first
    let comp = deduped.get(componentName);

    // Case-insensitive fallback
    if (!comp) {
      const nameLower = componentName.toLowerCase();
      for (const [key, value] of deduped) {
        if (key.toLowerCase() === nameLower) {
          comp = value;
          break;
        }
      }
    }

    // Fallback: search ALL components (including child items like TableRow, TableDefaultColumn)
    if (!comp) {
      const nameLower = componentName.toLowerCase();
      const childComp = components.find(
        (c: IComponent) =>
          !c.isDeprecated &&
          (isReportMode ? c.isReportDesignComponent : !c.isReportDesignComponent) &&
          (c.name === componentName || c.name.toLowerCase() === nameLower)
      );
      if (childComp && childComp.isItChildItem) {
        return {
          component: buildCatalogEntry(childComp, isReportMode, projectType, isWebScreen),
          isChildComponent: true,
          note: `${componentName} is a child component — it cannot be added directly. It is auto-created when its parent container is added.`
        };
      }
      // Known to the component set but filtered out of the addable catalog —
      // say WHY, so the caller stops retrying a name that purposes/docs mention.
      const excluded: IComponent | undefined = components.find(
        (c: IComponent): boolean => c.name === componentName || c.name.toLowerCase() === nameLower
      );
      if (excluded) {
        const reason: string =
          placementExclusionReason(excluded, {
            projectType,
            isWebScreen: isWebScreen ?? true,
            isReportMode,
            reactNativeOffered: isReactNativeGenerationOffered()
          }) ?? "it is filtered out";
        return {
          error: `Component "${excluded.name}" is not available in the ${designMode} catalog of this project: ${reason}.`,
          isUnavailable: true,
          unavailableReason: reason,
          note: `${excluded.name} exists in the platform but cannot be added here. Do not retry it — pick an available alternative via the category listing.`
        };
      }
      const knownName: string | undefined = Object.keys(COMPONENT_PURPOSES).find(
        (key: string): boolean => key.toLowerCase() === nameLower
      );
      if (knownName) {
        return {
          error: `Component "${knownName}" is not available in this project: it is not part of the project's component set.`,
          isUnavailable: true,
          unavailableReason: "it is not part of the project's component set",
          note: `${knownName} is a known platform component but this project/app type does not ship it. Pick an available alternative via the category listing.`
        };
      }
      return { error: `Component "${componentName}" not found in ${designMode} catalog` };
    }

    return { component: buildCatalogEntry(comp, isReportMode, projectType, isWebScreen) };
  }

  // Filter by category
  if (category) {
    const categoryLower = category.toLowerCase();
    const matches: IComponentCatalogEntry[] = [];
    for (const component of deduped.values()) {
      if (component.componentCategory && component.componentCategory.toLowerCase().includes(categoryLower)) {
        matches.push(buildCatalogEntry(component, isReportMode, projectType, isWebScreen));
      }
    }

    if (matches.length === 0) {
      return {
        error: `No components found for category "${category}" in ${designMode} mode`,
        availableCategories: getCategories(components, isReportMode, projectType, isWebScreen)
      };
    }

    return {
      category,
      designMode,
      count: matches.length,
      components: matches.map((entry) => ({
        name: entry.name,
        purpose: entry.purpose,
        isContainer: entry.isContainer,
        isFormField: entry.isFormField,
        hasValidation: entry.hasValidation,
        keyProperties: entry.keyProperties.slice(0, 5).map((p) => p.name),
        availableEvents: entry.availableEvents
      }))
    };
  }

  // No params — return category summary
  const categories = getCategories(components, isReportMode, projectType, isWebScreen);
  return {
    designMode,
    totalComponents: deduped.size,
    categories: categories.map((cat) => {
      const catComponents: Array<{ name: string; purpose: string | null }> = [];
      for (const component of deduped.values()) {
        if (component.componentCategory === cat) {
          const purposeKey = isReportMode ? `${component.name}__report` : component.name;
          const purpose = COMPONENT_PURPOSES[purposeKey] || COMPONENT_PURPOSES[component.name] || null;
          catComponents.push({ name: component.name, purpose });
        }
      }
      return {
        category: cat,
        count: catComponents.length,
        components: catComponents
      };
    })
  };
}
